/**
 * @file Vue composable for error handling and user experience
 * @version 1.0.0
 * @author Matthieu Chareton
 * @since 2025-07-08
 */

import {
  AirliftError,
  ErrorRecovery,
  ErrorFormatter,
} from '../utils/errors.js';

const { ref, reactive, computed, watch, onErrorCaptured, readonly } = Vue;

/**
 * Global error state management
 */
const globalErrorState = reactive({
  errors: [],
  notifications: [],
});

/**
 * Composable for error handling in Vue components
 */
export function useErrorHandler() {
  const errors = ref([]);
  const hasErrors = computed(() => errors.value.length > 0);
  const latestError = computed(
    () => errors.value[errors.value.length - 1] || null
  );

  /**
   * Handle an error with user-friendly messaging
   * @param error
   * @param context
   */
  const handleError = async (error, context = {}) => {
    let processedError;

    // Convert regular errors to AirliftError
    if (!(error instanceof AirliftError)) {
      processedError = new AirliftError(
        error.message || 'Unknown error',
        'UNKNOWN',
        null,
        error
      );
    } else {
      processedError = error;
    }

    // Add to local errors
    const errorEntry = {
      id: Date.now() + Math.random(),
      error: processedError,
      context,
      timestamp: new Date(),
      formatted: ErrorFormatter.formatForUser(processedError),
      dismissed: false,
    };

    errors.value.push(errorEntry);

    // Add to global state
    globalErrorState.errors.push(errorEntry);

    // Log error
    console.error(
      '[Error Handler]',
      ErrorFormatter.formatForLogging(processedError, context)
    );

    // Attempt recovery if possible
    if (ErrorRecovery.isRecoverable(processedError)) {
      try {
        const recovery = await ErrorRecovery.attemptRecovery(
          processedError,
          context
        );
        if (recovery.success) {
          console.log('[Error Recovery]', recovery.message);
        }
      } catch (recoveryError) {
        console.error('[Recovery Failed]', recoveryError);
      }
    }

    return errorEntry;
  };

  /**
   * Clear a specific error
   * @param errorId
   */
  const clearError = errorId => {
    const index = errors.value.findIndex(e => e.id === errorId);
    if (index !== -1) {
      errors.value.splice(index, 1);
    }

    const globalIndex = globalErrorState.errors.findIndex(
      e => e.id === errorId
    );
    if (globalIndex !== -1) {
      globalErrorState.errors.splice(globalIndex, 1);
    }
  };

  /**
   * Clear all errors
   */
  const clearAllErrors = () => {
    errors.value = [];
    globalErrorState.errors = [];
  };

  /**
   * Dismiss an error (mark as dismissed but keep in history)
   * @param errorId
   */
  const dismissError = errorId => {
    const error = errors.value.find(e => e.id === errorId);
    if (error) {
      error.dismissed = true;
    }

    const globalError = globalErrorState.errors.find(e => e.id === errorId);
    if (globalError) {
      globalError.dismissed = true;
    }
  };

  /**
   * Get active (non-dismissed) errors
   */
  const activeErrors = computed(() => errors.value.filter(e => !e.dismissed));

  /**
   * Retry an operation with error handling
   * @param operation
   * @param maxRetries
   */
  const retryOperation = async (operation, maxRetries = 3) => {
    try {
      return await ErrorRecovery.retryWithBackoff(operation, maxRetries);
    } catch (error) {
      await handleError(error, { operation: 'retry', maxRetries });
      throw error;
    }
  };

  /**
   * Wrap an async operation with error handling
   * @param operation
   * @param context
   */
  const withErrorHandling = (operation, context = {}) => {
    return async (...args) => {
      try {
        return await operation(...args);
      } catch (error) {
        await handleError(error, context);
        throw error;
      }
    };
  };

  return {
    errors: readonly(errors),
    hasErrors,
    latestError,
    activeErrors,
    handleError,
    clearError,
    clearAllErrors,
    dismissError,
    retryOperation,
    withErrorHandling,
  };
}

/**
 * Composable for error boundaries in Vue components
 */
export function useErrorBoundary() {
  const { handleError } = useErrorHandler();
  const boundaryErrors = ref([]);

  // Capture errors in child components
  onErrorCaptured((error, instance, info) => {
    const boundaryError = {
      id: Date.now() + Math.random(),
      error,
      component: instance?.$options.name || 'Unknown Component',
      info,
      timestamp: new Date(),
    };

    boundaryErrors.value.push(boundaryError);

    handleError(error, {
      component: boundaryError.component,
      info,
      boundary: true,
    });

    // Return false to prevent the error from propagating further
    return false;
  });

  const hasBoundaryErrors = computed(() => boundaryErrors.value.length > 0);

  const clearBoundaryErrors = () => {
    boundaryErrors.value = [];
  };

  return {
    boundaryErrors: readonly(boundaryErrors),
    hasBoundaryErrors,
    clearBoundaryErrors,
  };
}

/**
 * Composable for toast notifications
 */
export function useNotifications() {
  const addNotification = (message, type = 'info', duration = 5000) => {
    const notification = {
      id: Date.now() + Math.random(),
      message,
      type, // 'success', 'error', 'warning', 'info'
      timestamp: new Date(),
      duration,
      dismissed: false,
    };

    globalErrorState.notifications.push(notification);

    // Auto-dismiss after duration
    if (duration > 0) {
      setTimeout(() => {
        dismissNotification(notification.id);
      }, duration);
    }

    return notification;
  };

  const dismissNotification = notificationId => {
    const globalIndex = globalErrorState.notifications.findIndex(
      n => n.id === notificationId
    );
    if (globalIndex !== -1) {
      globalErrorState.notifications.splice(globalIndex, 1);
    }
  };

  const clearAllNotifications = () => {
    globalErrorState.notifications = [];
  };

  // Helper methods for common notification types
  const success = (message, duration = 3000) =>
    addNotification(message, 'success', duration);
  const error = (message, duration = 8000) =>
    addNotification(message, 'error', duration);
  const warning = (message, duration = 5000) =>
    addNotification(message, 'warning', duration);
  const info = (message, duration = 4000) =>
    addNotification(message, 'info', duration);

  return {
    notifications: computed(() => globalErrorState.notifications),
    addNotification,
    dismissNotification,
    clearAllNotifications,
    success,
    error,
    warning,
    info,
  };
}

/**
 * Composable for loading states
 */
export function useLoadingState() {
  const loadingStates = reactive({});

  const setLoading = (key, value = true) => {
    loadingStates[key] = value;
  };

  const isLoading = key => {
    return loadingStates[key] === true;
  };

  const isAnyLoading = computed(() => {
    return Object.values(loadingStates).some(state => state === true);
  });

  const clearLoading = key => {
    delete loadingStates[key];
  };

  const clearAllLoading = () => {
    Object.keys(loadingStates).forEach(key => {
      delete loadingStates[key];
    });
  };

  /**
   * Wrap an async operation with loading state
   * @param key
   * @param operation
   */
  const withLoading = (key, operation) => {
    return async (...args) => {
      setLoading(key, true);
      try {
        return await operation(...args);
      } finally {
        setLoading(key, false);
      }
    };
  };

  return {
    setLoading,
    isLoading,
    isAnyLoading,
    clearLoading,
    clearAllLoading,
    withLoading,
  };
}

/**
 * Global error and notification access
 */
export function useGlobalErrorState() {
  return {
    globalErrors: readonly(globalErrorState.errors),
    globalNotifications: readonly(globalErrorState.notifications),
    hasGlobalErrors: computed(() => globalErrorState.errors.length > 0),
    hasGlobalNotifications: computed(
      () => globalErrorState.notifications.length > 0
    ),
  };
}
